/**
 * InviteStaff Modal (Sprint 5 S5-B-pages)
 *
 * Modal-form surface for the FI Staff invitation flow. Composes
 * `@aaimplatform/ui` Dialog, Input, Select, and Button primitives with
 * zero local re-implementations.
 *
 * Sprint 1 schema deviation #1 honored: `roleSlug` is a single VARCHAR
 * (single-role-per-invite). Multi-role grants happen post-acceptance via
 * `useUpdateStaffRoles` from the staff detail page.
 *
 * Invitation lifecycle (GH #2307): `invite()` writes an Invitation row
 * only. No staff record exists until the invitee accepts the emailed
 * token; acceptance creates the TenantUser as ACTIVE with the invited
 * role, on either tenant path. On a credential-path tenant
 * (`credentialAuthEnabled`) the password set on the acceptance page becomes
 * the sign-in credential; on any other tenant it is ignored and the invitee
 * signs in through the institution's IdP afterwards
 * (auth-totp.openapi.yaml AcceptInvitationRequest.password). MX-Auth
 * acceptance through the staff bank login is the last paragraph of the
 * copy. Until acceptance the invitation is listed under Outstanding
 * invitations on the User Management page. The copy describes every path,
 * since this form does not know which one the tenant uses.
 *
 * The parent (UserManagement page) owns the open/close lifecycle and the
 * success notification. This component renders the form, validates input
 * client-side, and invokes `useInviteStaff` on submit.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  Input,
  Select,
  type SelectOption,
} from '@aaimplatform/ui';
import { useInviteStaff } from '../../hooks/useStaffLifecycle';
import { toAssignableStaffRoles, useRoleCatalog } from '../../hooks/useRoleCatalog';

/**
 * Preselected when the tenant's role catalog offers it. When it does not, the
 * select starts empty and the operator picks a role.
 */
const DEFAULT_ROLE_SLUG = 'fi_loan_officer';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteStaffModalProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Visibility change callback. */
  onOpenChange: (open: boolean) => void;
  /**
   * Success callback invoked after the invitation mutation resolves. The
   * parent renders the toast / banner and closes the dialog. Receives
   * the invitee email so the parent can compose operator copy.
   */
  onInvited: (email: string) => void;
}

/**
 * InviteStaffModal -- Sprint 5 invite-form surface. Pure UI + the
 * `useInviteStaff` mutation; the parent owns the open/close lifecycle
 * and the post-success notification.
 */
export function InviteStaffModal({
  open,
  onOpenChange,
  onInvited,
}: InviteStaffModalProps) {
  const [email, setEmail] = useState('');
  const [roleSlug, setRoleSlug] = useState<string>('');
  const [touched, setTouched] = useState({ email: false, roleSlug: false });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const inviteMutation = useInviteStaff();
  // GH #2318: the options come from the live role catalog, so an invite can
  // name any tenant-tier role the server grants, not a fixed three.
  const roleCatalogQuery = useRoleCatalog({ enabled: open });
  const roleOptions: SelectOption[] = useMemo(
    () =>
      toAssignableStaffRoles(roleCatalogQuery.data ?? []).map((role) => ({
        value: role.slug,
        label: role.name,
      })),
    [roleCatalogQuery.data],
  );

  const catalogEmpty =
    roleCatalogQuery.isSuccess && roleOptions.length === 0;

  // Preselect the default role once the catalog has loaded, only if the
  // catalog offers it and the operator has not picked one.
  useEffect(() => {
    if (open && roleSlug === '') {
      const fallback = roleOptions.find(
        (opt) => opt.value.toLowerCase() === DEFAULT_ROLE_SLUG,
      );
      if (fallback) setRoleSlug(fallback.value);
    }
  }, [open, roleSlug, roleOptions]);

  // Reset transient state when the dialog closes so a re-open starts
  // clean. Prevents the prior invitee's email leaking into the next
  // invitation form.
  useEffect(() => {
    if (!open) {
      setEmail('');
      setRoleSlug('');
      setTouched({ email: false, roleSlug: false });
      setSubmitError(null);
      inviteMutation.reset();
    }
    // intentionally exclude inviteMutation -- reset() is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedEmail = email.trim();
  const emailValid = EMAIL_PATTERN.test(trimmedEmail);
  const roleValid =
    roleSlug !== '' &&
    roleOptions.some((opt) => opt.value.toLowerCase() === roleSlug.toLowerCase());
  const formValid = emailValid && roleValid;

  const showEmailError = touched.email && !emailValid;
  const showRoleError = touched.roleSlug && !roleValid;

  const handleSubmit = async () => {
    setTouched({ email: true, roleSlug: true });
    if (!formValid || inviteMutation.isPending) return;
    setSubmitError(null);
    try {
      await inviteMutation.mutateAsync({
        email: trimmedEmail,
        roleSlug,
      });
      onInvited(trimmedEmail);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send invitation.';
      setSubmitError(message);
    }
  };

  const handleCancel = () => {
    if (inviteMutation.isPending) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite a staff member"
      size="md"
      isDismissable={!inviteMutation.isPending}
      isKeyboardDismissDisabled={inviteMutation.isPending}
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={handleCancel}
            disabled={inviteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!formValid || inviteMutation.isPending}
          >
            {inviteMutation.isPending ? 'Sending...' : 'Send invitation'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {submitError && (
          <Alert type="error" title="Invitation failed">
            {submitError}
          </Alert>
        )}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (!touched.email) setTouched((t) => ({ ...t, email: true }));
          }}
          onBlur={() => setTouched((t) => ({ ...t, email: true }))}
          required
          fullWidth
          autoComplete="off"
          spellCheck={false}
          placeholder="newhire@institution.example"
          error={
            showEmailError ? 'Provide a valid email address.' : undefined
          }
          helperText="The invitation email is sent to this address. The token expires after 14 days."
        />

        {roleCatalogQuery.isError && (
          <Alert type="error" title="Role list unavailable">
            {roleCatalogQuery.error?.message ?? 'The role list could not be loaded.'}
          </Alert>
        )}

        <Select
          label="Role"
          options={roleOptions}
          placeholder={roleCatalogQuery.isPending ? 'Loading roles...' : 'Select a role'}
          disabled={roleOptions.length === 0}
          value={roleSlug}
          onChange={(event) => {
            setRoleSlug(event.target.value);
            if (!touched.roleSlug)
              setTouched((t) => ({ ...t, roleSlug: true }));
          }}
          required
          fullWidth
          error={showRoleError ? 'Select a role.' : undefined}
          helperText={
            catalogEmpty
              ? 'No assignable roles were returned for this institution, so an invitation cannot be sent yet.'
              : 'Single role on invite. Additional roles can be granted from the staff detail page after acceptance.'
          }
        />

        <div className="rounded-sm border border-recessed bg-canvas px-4 py-3 space-y-2">
          <p className="text-xs font-sans uppercase tracking-[0.15em] text-tertiary mb-1">
            What happens next
          </p>
          <p className="text-sm font-sans text-secondary">
            The invitee receives an acceptance link by email. Until they
            accept, the invitation is listed under Outstanding invitations,
            where you can resend or revoke it.
          </p>
          <p className="text-sm font-sans text-secondary">
            Accepting makes the invitee an Active staff member with the role
            chosen here. If your staff sign in to Aaim with a password, the
            invitee sets one when accepting. If your staff sign in through
            your institution&apos;s identity provider, the invitee signs in
            there after accepting.
          </p>
          {/* MX-Auth contextual help (ADR-013). Rendered for staff-manager
              role holders only -- this modal is reachable solely from the
              admin-gated User Management page. */}
          <p className="text-sm font-sans text-secondary">
            If your institution uses staff sign-in with your institution&apos;s
            online banking (MX-Auth), the invitee completes acceptance by
            signing into your institution&apos;s online banking on the staff
            sign-in page. The bank login proves they are a customer of your
            institution; the emailed invitation proves they control the
            invited address. Staff sign-in verifies identity only -- it never
            reads or stores account balances or transactions.
          </p>
        </div>
      </div>
    </Dialog>
  );
}

export default InviteStaffModal;
